import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import {
  deleteO365VisioUsage,
  getO365VisioUsageById,
  saveO365VisioUsage,
  searchO365VisioUsage,
} from './o365VisioUsage.action';
import { IO365VisioUsageState } from './o365VisioUsage.model';
import { IO365VisioUsage } from '../../../services/o365/o365VisioUsage/o365VisioUsage.model';

export const initialState: IO365VisioUsageState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: 'o365-visio-usage',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const o365VisioUsageSlice = createSlice({
  name: 'o365VisioUsage',
  initialState,
  reducers: {
    clearO365VisioUsage: () => {
      return initialState;
    },
    clearO365VisioUsageMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearO365VisioUsageGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchO365VisioUsage.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchO365VisioUsage.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<IO365VisioUsage>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchO365VisioUsage.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getO365VisioUsageById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getO365VisioUsageById.fulfilled.type]: (state, action: PayloadAction<IO365VisioUsage>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getO365VisioUsageById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveO365VisioUsage.pending.type]: (state) => {
      state.save.loading = true;
    },
    [saveO365VisioUsage.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload?.messages || [];
    },
    [saveO365VisioUsage.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteO365VisioUsage.pending.type]: (state) => {
      state.delete.loading = true;
    },
    [deleteO365VisioUsage.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload?.messages || [];
    },
    [deleteO365VisioUsage.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

export const {
  clearO365VisioUsage,
  clearO365VisioUsageMessages,
  clearO365VisioUsageGetById,
  setTableColumnSelection,
} = o365VisioUsageSlice.actions;

export const o365VisioUsageSelector = (state: RootState) => state.o365VisioUsage;

export default o365VisioUsageSlice.reducer;
